/* Editorial recommendations for cards created from public product JSON. */
const CARD_SELECTOR = 'product-card, product-card-mini, .loew-selection__card, .loew-discovery__card';
const ROOT_SELECTOR = '.loew-editorial-recommendation';
const OWN_SELECTOR = '[data-loew-editorial-hydrated]';
const CACHE_LIMIT = 80;
const SUCCESS_TTL = 120000;
const FAILURE_TTL = 30000;

export function productIdentity(value, origin) {
  try {
    const url = new URL(value, origin);
    if (url.origin !== new URL(origin).origin || url.username || url.password) return null;
    const match = url.pathname.match(/^(\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*))?\/(?:collections\/[^/]+\/)?products\/([^/]+)\/?$/i);
    if (!match) return null;
    const handle = decodeURIComponent(match[2]);
    if (!handle || /[\/\\?#\u0000-\u001f]/.test(handle) || handle === '.' || handle === '..') return null;
    const path = `${match[1] || ''}/products/${encodeURIComponent(handle)}`;
    const canonical = new URL(path, url.origin);
    const request = new URL(canonical);
    request.searchParams.set('view', 'loew-recommendation');
    return { handle, path, canonical: canonical.href, request: request.href };
  } catch (_) {
    return null;
  }
}

function outsideQuickShop(card, selector) {
  return Array.from(card.querySelectorAll(selector)).filter(node => !node.closest('quick-shop') && node.closest(CARD_SELECTOR) === card);
}

function cardIdentity(card, origin) {
  const links = card.matches('a[href]') ? [card] : outsideQuickShop(card,
    card.matches('product-card, product-card-mini') ? 'a[product-card-link][href]' :
      card.matches('.loew-selection__card') ? 'h3 a[href], a.loew-selection__image[href]' : 'a.loew-discovery__product[href]');
  const identities = links.map(link => productIdentity(link.getAttribute('href'), origin)).filter(Boolean);
  if (!identities.length || new Set(identities.map(value => value.canonical)).size > 1) return null;
  const identity = identities[0];
  const marker = outsideQuickShop(card, '[data-loew-benefit-product]')[0];
  const ids = [card.getAttribute('data-product-id'), card.getAttribute('product-id'), marker?.getAttribute('data-loew-benefit-product')].filter(Boolean);
  if (ids.some(value => !/^[1-9]\d*$/.test(value)) || new Set(ids).size > 1) return null;
  const rawId = ids[0];
  return { ...identity, id: rawId || null, key: `${identity.canonical}|${rawId || ''}` };
}

function safeRecommendation(node) {
  const allowedTags = new Set(['DIV', 'SPAN', 'SVG', 'PATH', 'STYLE', 'P', 'BR']);
  const mode = node.getAttribute('data-loew-recommendation-mode') ?? 'stars';
  if (mode !== 'stars' && mode !== 'text') return false;
  const stars = node.querySelectorAll('svg').length;
  if (mode === 'stars' && stars !== 5 || mode === 'text' && stars !== 0) return false;
  if (mode === 'text') {
    const reasons = node.querySelectorAll('.loew-editorial-recommendation__reason');
    if (reasons.length !== 1 || !reasons[0].textContent.trim() || node.querySelector('[role="img"], .loew-editorial-recommendation__stars')) return false;
  }
  for (const element of [node, ...node.querySelectorAll('*')]) {
    if (!allowedTags.has(element.tagName.toUpperCase())) return false;
    for (const attribute of element.attributes) {
      const name = attribute.name.toLowerCase();
      if (/^on/.test(name) || ['href', 'xlink:href', 'src', 'srcdoc', 'style'].includes(name)) return false;
      if (name === 'fill' && attribute.value !== 'currentColor') return false;
      if (!/^(?:class|role|aria-[\w-]+|data-loew-[\w-]+|data-product-id|data-product-handle|data-shopify|width|height|viewbox|fill|d|xmlns|focusable)$/.test(name)) return false;
    }
    if (element.tagName.toUpperCase() === 'STYLE') {
      const css = element.textContent;
      if (css.length > 10000 || /@|url\s*\(|expression\s*\(|behavior\s*:|-moz-binding/i.test(css)) return false;
      for (const block of css.split('}')) {
        const selector = block.split('{')[0].trim();
        if (selector && selector.split(',').some(part => !/^\.loew-editorial-recommendation(?:__[a-z0-9_-]+)?$/.test(part.trim()))) return false;
      }
    }
  }
  return true;
}

export function parseRecommendation(text, requested, responseURL, Parser) {
  const finalURL = new URL(responseURL);
  const finalIdentity = productIdentity(finalURL.href, requested.canonical);
  if (!finalIdentity || finalIdentity.canonical !== requested.canonical || finalURL.searchParams.get('view') !== 'loew-recommendation') throw new Error('Unexpected recommendation response URL');
  if (text.length > 64000) throw new Error('Recommendation response too large');
  const parsed = new Parser().parseFromString(text, 'text/html');
  const fragments = parsed.querySelectorAll('[data-loew-recommendation-fragment]');
  if (fragments.length !== 1) throw new Error('Unexpected recommendation fragment');
  const fragment = fragments[0];
  const id = fragment.getAttribute('data-product-id');
  if (!/^[1-9]\d*$/.test(id || '') || fragment.getAttribute('data-product-handle') !== requested.handle || requested.id && requested.id !== id) throw new Error('Recommendation identity mismatch');
  const recommendations = fragment.querySelectorAll(ROOT_SELECTOR);
  if (recommendations.length > 1) throw new Error('Duplicate recommendation fragment');
  const node = recommendations[0] || null;
  if (node && node.getAttribute('data-loew-recommendation-product-id') !== id) throw new Error('Recommendation markup identity mismatch');
  if (node && !safeRecommendation(node)) throw new Error('Invalid recommendation markup');
  // Old cached fragments may still contain plain highlights or star reasons.
  // Keep those saved texts off every list card while preserving PDP data.
  if (node?.getAttribute('data-loew-recommendation-mode') === 'text') return { id, handle: requested.handle, node: null };
  node?.querySelectorAll('.loew-editorial-recommendation__reason').forEach(reason => reason.remove());
  return { id, handle: requested.handle, node };
}

export function createRecommendationHydrator(environment) {
  const { document, location, fetch, DOMParser, IntersectionObserver, MutationObserver } = environment;
  const setTimer = environment.setTimeout || globalThis.setTimeout;
  const clearTimer = environment.clearTimeout || globalThis.clearTimeout;
  const now = environment.now || Date.now;
  const states = new WeakMap();
  const observed = new WeakSet();
  const visibleCards = new WeakMap();
  const pendingCards = new Set();
  const cache = new Map();
  const requests = new Set();
  const networkQueue = [];
  let activeRequests = 0;
  let mutationObserver;
  let intersectionObserver;
  let scanTimer;
  let stopped = false;

  function network(task) {
    return new Promise((resolve, reject) => {
      networkQueue.push({ task, resolve, reject });
      drainNetwork();
    });
  }

  function drainNetwork() {
    while (!stopped && activeRequests < 4 && networkQueue.length) {
      const item = networkQueue.shift();
      activeRequests += 1;
      Promise.resolve().then(item.task).then(item.resolve, item.reject).finally(() => {
        activeRequests -= 1;
        drainNetwork();
      });
    }
  }

  function load(identity) {
    const saved = cache.get(identity.canonical);
    if (saved && saved.until > now()) return saved.promise;
    const entry = { until: Infinity, promise: null };
    entry.promise = network(async () => {
      const controller = new AbortController();
      requests.add(controller);
      const timeout = setTimer(() => controller.abort(), 8000);
      try {
        const response = await fetch(identity.request, { mode: 'same-origin', credentials: 'omit', signal: controller.signal, redirect: 'follow' });
        if (!response.ok) throw new Error('Recommendation request failed');
        return parseRecommendation(await response.text(), { ...identity, id: null }, response.url, DOMParser);
      } finally {
        clearTimer(timeout);
        requests.delete(controller);
      }
    }).then(value => {
      entry.until = now() + SUCCESS_TTL;
      return value;
    }, error => {
      entry.until = now() + FAILURE_TTL;
      throw error;
    });
    cache.delete(identity.canonical);
    cache.set(identity.canonical, entry);
    while (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
    return entry.promise;
  }

  function removeOwned(card) {
    outsideQuickShop(card, OWN_SELECTOR).forEach(node => node.remove());
  }

  function mark(card, value, productId) {
    card.setAttribute('data-loew-editorial-state', value);
    if (productId) card.setAttribute('data-loew-editorial-product-id', productId);
    else card.removeAttribute('data-loew-editorial-product-id');
  }

  function mount(card, node) {
    const title = outsideQuickShop(card, 'product-card-title, .loew-selection__content h3, .loew-discovery__name')[0];
    if (!title) return false;
    const heading = title.closest('h3, h4') || title;
    const link = heading.parentElement?.tagName === 'A' ? heading.parentElement : null;
    const anchor = link && link !== card && card.contains(link) ? link : heading;
    anchor.after(node);
    return true;
  }

  async function hydrateCard(card) {
    if (stopped || !card.isConnected) return;
    const identity = cardIdentity(card, location.origin);
    let state = states.get(card);
    if (!identity || state && state.key !== identity.key) {
      removeOwned(card);
      states.delete(card);
      state = null;
      if (!identity) {
        mark(card, 'failed');
        return;
      }
    }
    // Normalize pre-existing editorial markup too, including cached SSR cards.
    outsideQuickShop(card, ROOT_SELECTOR).forEach(node => {
      if (node.getAttribute('data-loew-recommendation-mode') === 'text') node.remove();
      else node.querySelectorAll('.loew-editorial-recommendation__reason').forEach(reason => reason.remove());
    });
    const nativeRoot = outsideQuickShop(card, ROOT_SELECTOR).find(node => !node.hasAttribute('data-loew-editorial-hydrated'));
    if (nativeRoot) {
      removeOwned(card);
      mark(card, 'skipped-native');
      return;
    }
    if (card.matches('product-card[data-product-id], product-card-mini[data-product-id]')) {
      mark(card, 'skipped-native');
      return;
    }
    if (state && (state.pending || state.done || state.retryAfter > now())) return;
    state = { key: identity.key, pending: true, done: false, retryAfter: 0 };
    states.set(card, state);
    mark(card, 'pending');
    try {
      const result = await load(identity);
      const current = cardIdentity(card, location.origin);
      if (stopped || !card.isConnected || states.get(card) !== state || current?.key !== identity.key) return;
      if (identity.id && result.id !== identity.id) throw new Error('Recommendation card identity changed');
      if (outsideQuickShop(card, ROOT_SELECTOR).some(node => !node.hasAttribute('data-loew-editorial-hydrated'))) return;
      removeOwned(card);
      if (result.node) {
        const copy = document.importNode(result.node, true);
        copy.setAttribute('data-loew-editorial-hydrated', '');
        if (!mount(card, copy)) throw new Error('No recommendation card title');
      }
      state.done = true;
      mark(card, result.node ? 'ready' : 'none', result.id);
    } catch (_) {
      if (states.get(card) === state) {
        removeOwned(card);
        state.retryAfter = now() + FAILURE_TTL;
        mark(card, 'failed');
      }
    } finally {
      state.pending = false;
    }
  }

  function watchCard(card) {
    if (card.closest('quick-shop')) return;
    if (!observed.has(card)) {
      observed.add(card);
      if (intersectionObserver) intersectionObserver.observe(card);
      else void hydrateCard(card);
    } else if (!intersectionObserver || visibleCards.get(card)) {
      void hydrateCard(card);
    }
  }

  function scan(root = document) {
    if (stopped) return;
    if (root.matches?.(CARD_SELECTOR)) watchCard(root);
    root.querySelectorAll?.(CARD_SELECTOR).forEach(watchCard);
  }

  function queueCard(card) {
    if (!card || card.closest('quick-shop')) return;
    pendingCards.add(card);
    if (!scanTimer) scanTimer = setTimer(() => {
      scanTimer = null;
      pendingCards.forEach(watchCard);
      pendingCards.clear();
    }, 50);
  }

  function start() {
    if (IntersectionObserver) intersectionObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      visibleCards.set(entry.target, entry.isIntersecting);
      if (entry.isIntersecting) void hydrateCard(entry.target);
    }), { rootMargin: '200px' });
    mutationObserver = new MutationObserver(records => records.forEach(record => {
      if (record.target.closest?.(OWN_SELECTOR)) return;
      queueCard(record.target.closest?.(CARD_SELECTOR));
      record.addedNodes?.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.matches(CARD_SELECTOR)) queueCard(node);
        node.querySelectorAll(CARD_SELECTOR).forEach(queueCard);
      });
    }));
    mutationObserver.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['href', 'data-product-id', 'product-id'] });
    scan();
  }

  function stop() {
    stopped = true;
    intersectionObserver?.disconnect();
    mutationObserver?.disconnect();
    if (scanTimer) clearTimer(scanTimer);
    requests.forEach(controller => controller.abort());
    networkQueue.splice(0).forEach(item => item.reject(new Error('Hydrator stopped')));
    cache.clear();
  }

  return { start, stop, scan, hydrateCard };
}

if (typeof window !== 'undefined' && window.document) {
  const boot = () => createRecommendationHydrator({ document, location, fetch: window.fetch.bind(window), DOMParser, IntersectionObserver: window.IntersectionObserver, MutationObserver: window.MutationObserver }).start();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
